import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import CountrySelect from "../../../components/ui/CountrySelect";
import DatePickerField from "../../../components/ui/DatePickerField";
import ClientPhoneField from "../../../components/clients/ClientPhoneField";
import IndividualNamePreview from "../../../components/clients/IndividualNamePreview";
import Lottie from "lottie-react";
import DocumentPrefillMark from "./DocumentPrefillMark";
import { isPrefilledCustomerField } from "../../../utils/companyReadingPrefill";

import iconPassport from "../../../images/passport.svg";
import iconDateOfBirth from "../../../images/birthday.svg";
import iconAddress from "../../../images/address.svg";
import iconEarthLocation from "../../../images/earth-location.svg";
import iconEmail from "../../../images/email_icon.svg";
import iconPhoneCall from "../../../images/phone-call.svg";
import iconWebsite from "../../../images/internet.svg";
import iconBusinessActivity from "../../../images/briefcaseX.svg";
import iconEntityNameJson from "../../../images/building-lottie.json";
import iconNameJson from "../../../images/individual-lottie.json";

import "../css/KycSectionsStyle.css";

const USE_OLD_STANDARD_CUSTOMER_DETAILS_LAYOUT = false;

function readMapboxToken() {
  const raw =
    process.env.REACT_APP_MAPBOX_TOKEN ||
    process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
    "";

  return String(raw).trim().replace(/^["']|["']$/g, "");
}

const MAPBOX_TOKEN = readMapboxToken();

function toDateOrNull(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === "function") return v.toDate(); // Firestore Timestamp
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function sanitizePhone(value) {
  return String(value || "").replace(/[^0-9+() ]/g, "");
}

function capitalizeFirstLetter(value) {
  return String(value || "").replace(/^(\s*)([a-z])/, (_, spaces, letter) => (
    `${spaces}${letter.toUpperCase()}`
  ));
}

function capitalizeNameWords(value) {
  return String(value || "").replace(
    /(^|[\s'-])([a-z])/g,
    (_, prefix, letter) => `${prefix}${letter.toUpperCase()}`
  );
}

function autoResizeTextarea(el) {
  if (!el || typeof window === "undefined") return;
  const computed = window.getComputedStyle(el);
  const lineHeight = parseFloat(computed.lineHeight) || 24;
  const paddingTop = parseFloat(computed.paddingTop) || 0;
  const paddingBottom = parseFloat(computed.paddingBottom) || 0;
  const borderTop = parseFloat(computed.borderTopWidth) || 0;
  const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
  const verticalExtras = paddingTop + paddingBottom + borderTop + borderBottom;
  const minHeight = lineHeight + verticalExtras;
  const maxHeight = lineHeight * 15 + verticalExtras;

  el.style.height = "auto";
  const contentHeight = el.scrollHeight;
  const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  el.style.height = `${nextHeight}px`;
  el.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
}

function isValidEmail(value) {
  const v = String(value || "").trim();
  if (!v) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function InlineDangerMessage({ children }) {
  return (
    <div className="kycInlineFieldError">
      <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />{" "}
      {children}
    </div>
  );
}

function countryCodeToName(code) {
  const c = String(code || "")
    .trim()
    .toUpperCase();
  if (!c) return "";

  try {
    const dn = new Intl.DisplayNames(["en"], { type: "region" });
    return dn.of(c) || c;
  } catch {
    return c;
  }
}

function getContextEntry(feature, prefix) {
  const list = Array.isArray(feature?.context) ? feature.context : [];
  return list.find((item) => String(item?.id || "").startsWith(`${prefix}.`));
}

function toCountryCode(value) {
  const raw = String(value || "")
    .trim()
    .toUpperCase();
  if (!raw) return "";
  if (raw.length === 2) return raw;
  return raw.split("-").pop() || raw;
}

function parseMapboxFeature(feature) {
  if (!feature) return null;

  const countryCtx = getContextEntry(feature, "country");
  const placeCtx =
    getContextEntry(feature, "place") ||
    getContextEntry(feature, "locality") ||
    getContextEntry(feature, "district");

  const houseNumber = feature?.address ? `${feature.address} ` : "";
  const streetName =
    feature?.text ||
    feature?.properties?.address ||
    feature?.place_name?.split(",")?.[0] ||
    "";
  const firstLine = `${houseNumber}${streetName}`.trim() || feature?.place_name || "";

  return {
    id: String(feature.id || firstLine),
    label: feature.place_name || firstLine,
    address: firstLine,
    city: placeCtx?.text || "",
    country: toCountryCode(countryCtx?.short_code || countryCtx?.text || ""),
  };
}

function parseMapboxCityFeature(feature) {
  if (!feature) return null;

  const countryCtx = getContextEntry(feature, "country");
  const cityName =
    feature?.text ||
    feature?.place_name?.split(",")?.[0] ||
    feature?.properties?.name ||
    "";

  if (!cityName) return null;

  return {
    id: String(feature.id || cityName),
    city: cityName,
    country: toCountryCode(countryCtx?.short_code || countryCtx?.text || ""),
    label: feature.place_name || cityName,
  };
}

function KycCustomerDetailsFieldError({
  show,
  children = "This field is required",
}) {
  if (!show) return null;
  return <InlineDangerMessage>{children}</InlineDangerMessage>;
}

function KycCustomerDetailsTypeaheadField({
  kind = "address",
  value,
  placeholder,
  className = "",
  disabled = false,
  boxRef,
  loading = false,
  dropdownOpen = false,
  suggestions = [],
  onChange,
  onCommit,
  onSuggestionSelect,
}) {
  const isAddress = kind === "address";
  const rawValue = String(value || "").trim();
  const customOption =
    rawValue && !disabled
      ? {
          id: `__custom_${kind}__`,
          label: rawValue,
          ...(isAddress ? { address: rawValue } : { city: rawValue }),
          isCustomValue: true,
        }
      : null;
  const displaySuggestions = customOption
    ? [
        customOption,
        ...suggestions.filter((item) => {
          const itemPrimaryValue = String(
            isAddress ? item.address || item.label || "" : item.city || item.label || ""
          ).trim();
          if (isAddress) {
            return itemPrimaryValue.toLowerCase() !== rawValue.toLowerCase();
          }

          const itemLabelValue = String(item.label || itemPrimaryValue).trim();
          const rawLower = rawValue.toLowerCase();
          return !(
            itemPrimaryValue.toLowerCase() === rawLower &&
            itemLabelValue.toLowerCase() === rawLower
          );
        }),
      ]
    : suggestions;
  const shouldShowDropdown = dropdownOpen && displaySuggestions.length > 0;

  return (
    <div className="kycCustomerDetailsSuggestBox" ref={boxRef}>
      <input
        className={className}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter" || e.nativeEvent?.isComposing) return;
          e.preventDefault();
          onCommit?.();
        }}
        onBlur={() => {
          window.setTimeout(() => {
            onCommit?.();
          }, 120);
        }}
        onFocus={() => {
          if (suggestions.length > 0) {
            // caller controls actual open state via existing suggestions
          }
        }}
        disabled={disabled}
      />

      {loading ? (
        <div className="kycCustomerDetailsSuggestStatus">
          Searching {isAddress ? "address" : "city"}…
        </div>
      ) : null}

      {shouldShowDropdown ? (
        <div className="kycCustomerDetailsSuggestDropdown">
          {displaySuggestions.map((item) => (
            <button
              key={item.id || item.label || item.city}
              type="button"
              className={`kycCustomerDetailsSuggestItem${
                item.isCustomValue ? " kycCustomerDetailsSuggestItemCustom" : ""
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (item.isCustomValue) {
                  onCommit?.();
                  return;
                }
                onSuggestionSelect?.(item);
              }}
            >
              <div className="kycCustomerDetailsSuggestPrimary">
                {isAddress ? item.address || item.label : item.city || item.label}
              </div>
              <div className="kycCustomerDetailsSuggestSecondary">
                {item.isCustomValue
                  ? `Use entered ${isAddress ? "address" : "city"}`
                  : item.label}
              </div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function KycCustomerDetailsEditableCard({
  icon,
  lottie,
  label,
  children,
  className = "",
  iconWrapClassName = "",
  iconClassName = "",
  bodyClassName = "",
  topRightNote = null,
}) {
  return (
    <div className={`kycCustomerDetailsHybridCard ${className}`}>
      <div
        className={`kycCustomerDetailsHybridIcon ${iconWrapClassName}`.trim()}
      >
        {lottie ? (
          <Lottie
            animationData={lottie}
            loop
            autoplay
            className="kycCustomerDetailsHybridIconLottie"
          />
        ) : icon ? (
          <img
            src={icon}
            alt=""
            className={`kycCustomerDetailsHybridIconImg ${iconClassName}`.trim()}
          />
        ) : null}
      </div>

      <div
        className={`kycCustomerDetailsHybridContent ${bodyClassName}`.trim()}
      >
        {topRightNote ? (
          <div className="kycCustomerDetailsTopRightNote">{topRightNote}</div>
        ) : null}
        <div className="kycCustomerDetailsHybridLabel">{label}</div>
        {children}
      </div>
    </div>
  );
}

function KycSectionCustomerDetailsClientViewInspired({
  sectionNo,
  separateNames,
  customer,
  preferredPhoneCountry,
  copy,
  cc,
  readOnly,
  isEntity,
  useMainCountryOfOperation,
  missingName,
  missingNationality,
  missingMainCountryOfOperation,
  missingDob,
  missingAddress,
  missingCity,
  missingCountry,
  missingEmail,
  invalidEmail,
  showEmailBlurError,
  onEmailBlur,
  missingPhone,
  missingBusinessActivity,
  onSetAt,
  handleNationalityOrIncChange,
  handleMainCountryOfOperationChange,
  handleCountryChange,
  debouncedSyncIndustryAssessment,
  addressBoxRef,
  cityBoxRef,
  addressSuggestions,
  addressLoading,
  addressDropdownOpen,
  citySuggestions,
  cityLoading,
  cityDropdownOpen,
  handleAddressInput,
  handleCityInput,
  applyAddressSuggestion,
  applyCitySuggestion,
  commitCustomAddress,
  commitCustomCity,
}) {
  const businessActivityRef = useRef(null);
  const [openTooltip, setOpenTooltip] = useState(null);
  const showMainCountryOfOperation = isEntity && useMainCountryOfOperation;
  const topCountryValue = showMainCountryOfOperation
    ? customer.mainCountryOfOperation || ""
    : customer.nationalityOrIncorporationCountry || "";
  const topCountryLabel = showMainCountryOfOperation
    ? copy.labels.mainCountryOfOperation
    : copy.labels.nationalityOrInc;

  function handleCountryMouseEnter(tooltipId, el) {
    const span = el?.querySelector(".rs__single-value span");
    if (span && span.scrollWidth > span.clientWidth) setOpenTooltip(tooltipId);
  }

  function handleCountryMouseLeave() { setOpenTooltip(null); }

  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      autoResizeTextarea(businessActivityRef.current);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [customer.businessActivity]);

  return (
    <div className="card kycCard kycCustomerDetailsCard mb-3">
      <div className="kycSectionHead">
        {copy.header.kicker ? (
          <div className="kycSectionKicker">{copy.header.kicker}</div>
        ) : null}

        <h5 className="kycSectionTitle">
          {sectionNo}
          {copy.header.title}
        </h5>

        {copy.header.hint ? (
          <div className="kycSectionHint">{copy.header.hint}</div>
        ) : null}
      </div>

      <div className="kycSectionBody">
        <div className="row g-3">
          <div
            className={
              isEntity
                ? showMainCountryOfOperation
                  ? "col-5"
                  : "col-9"
                : "col-5"
            }
          >
            <KycCustomerDetailsEditableCard
              lottie={isEntity ? iconEntityNameJson : iconNameJson}
              label={<>{copy.labels.name}<DocumentPrefillMark show={isPrefilledCustomerField(customer, "name")} /></>}
              className="h-100 kycCustomerDetailsHybridCard--identity"
              iconWrapClassName="kycCustomerDetailsHybridIcon--lottie"
            >
              {separateNames && !isEntity ? (
                <IndividualNamePreview inputClass="form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--name" value={customer} displayName={customer.name} disabled={readOnly} onChange={(patch) => onSetAt(["customer"], { ...customer, ...patch })} />
              ) : (
                <input
                  className={`form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--name ${cc([
                    "customer",
                    "name",
                  ])}`}
                  value={customer.name || ""}
                  onChange={(e) =>
                    onSetAt(
                      ["customer", "name"],
                      isEntity
                        ? e.target.value
                        : capitalizeNameWords(e.target.value)
                    )
                  }
                  disabled={readOnly}
                />
              )}
              <KycCustomerDetailsFieldError show={missingName} />
            </KycCustomerDetailsEditableCard>
          </div>

          <div
            className={
              isEntity
                ? "col-3"
                : "col-3"
            }
          >
            <KycCustomerDetailsEditableCard
              icon={iconDateOfBirth}
              label={<>{copy.labels.dobOrInc}<DocumentPrefillMark show={isPrefilledCustomerField(customer, "dobOrIncorporationDate")} /></>}
              className="h-100 kycCustomerDetailsHybridCard--date"
              bodyClassName="kycCustomerDetailsHybridContent--date"
            >
              <div
                className={`kycCustomerDetailsHybridDate ${cc([
                  "customer",
                  "dobOrIncorporationDate",
                ])}`}
              >
                <DatePickerField
                  label={null}
                  value={toDateOrNull(customer.dobOrIncorporationDate)}
                  onChange={(val) =>
                    onSetAt(["customer", "dobOrIncorporationDate"], val)
                  }
                  placeholder="Select date"
                  dateFormat="d M yy"
                  appendTo="body"
                  disabled={readOnly}
                  hideTriggerButton
                  stagedView
                  className="kycCustomerDetailsHybridDatePicker"
                  inputClassName="kycCustomerDetailsHybridInput"
                />
              </div>
              <KycCustomerDetailsFieldError show={missingDob} />
            </KycCustomerDetailsEditableCard>
          </div>

          {!isEntity || showMainCountryOfOperation ? (
            <div className="col-4">
              <KycCustomerDetailsEditableCard
                icon={iconPassport}
                label={topCountryLabel}
                className="h-100"
              >
                <div
                  data-tooltip-id="cust-nationality"
                  data-tooltip-content={countryCodeToName(topCountryValue)}
                  onMouseEnter={(e) => handleCountryMouseEnter("cust-nationality", e.currentTarget)}
                  onMouseLeave={handleCountryMouseLeave}
                >
                  <CountrySelect
                    value={topCountryValue}
                    onChange={showMainCountryOfOperation ? handleMainCountryOfOperationChange : handleNationalityOrIncChange}
                    className={`w-100 country-customerDetails kycCustomerDetailsHybridCountrySelect ${cc([
                      "customer",
                      showMainCountryOfOperation
                        ? "mainCountryOfOperation"
                        : "nationalityOrIncorporationCountry",
                    ])}`}
                    optionTextClassName="countryTextOptionsCustomerDetails"
                    disabled={readOnly}
                  />
                </div>
                {topCountryValue ? <Tooltip id="cust-nationality" place="bottom" isOpen={openTooltip === "cust-nationality"} style={{ zIndex: 9999, backgroundColor: "#9E9692", borderRadius: 10, fontSize: 12 }} /> : null}
                <KycCustomerDetailsFieldError
                  show={
                    showMainCountryOfOperation
                      ? missingMainCountryOfOperation
                      : missingNationality
                  }
                />
              </KycCustomerDetailsEditableCard>
            </div>
          ) : null}

          <div className="col-8">
            <KycCustomerDetailsEditableCard
              icon={iconAddress}
              label={<>{copy.labels.address}<DocumentPrefillMark show={isPrefilledCustomerField(customer, "address") || isPrefilledCustomerField(customer, "city")} /></>}
              className="h-100"
              bodyClassName="kycCustomerDetailsHybridContent--stacked"
            >
              <div className="row g-3">
                <div className="col-8">
                  <KycCustomerDetailsTypeaheadField
                    kind="address"
                    boxRef={addressBoxRef}
                    className={`form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--line ${cc([
                      "customer",
                      "address",
                    ])}`}
                    value={customer.address || ""}
                    placeholder="Address"
                    onChange={handleAddressInput}
                    onCommit={commitCustomAddress}
                    suggestions={addressSuggestions}
                    loading={addressLoading}
                    dropdownOpen={addressDropdownOpen}
                    onSuggestionSelect={applyAddressSuggestion}
                    disabled={readOnly}
                  />
                  <KycCustomerDetailsFieldError show={missingAddress} />
                </div>

                <div className="col-4">
                  <KycCustomerDetailsTypeaheadField
                    kind="city"
                    boxRef={cityBoxRef}
                    className={`form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--line ${cc([
                      "customer",
                      "city",
                    ])}`}
                    value={customer.city || ""}
                    placeholder="City"
                    onChange={handleCityInput}
                    onCommit={commitCustomCity}
                    suggestions={citySuggestions}
                    loading={cityLoading}
                    dropdownOpen={cityDropdownOpen}
                    onSuggestionSelect={applyCitySuggestion}
                    disabled={readOnly}
                  />
                  <KycCustomerDetailsFieldError show={missingCity} />
                </div>
              </div>
            </KycCustomerDetailsEditableCard>
          </div>

          <div className="col-4">
            <KycCustomerDetailsEditableCard
              icon={iconEarthLocation}
              label={isEntity ? "Incorporation country" : "Country of domicile"}
              className="h-100"
            >
              <div
                data-tooltip-id="cust-country"
                data-tooltip-content={countryCodeToName(isEntity ? customer.nationalityOrIncorporationCountry : customer.country)}
                onMouseEnter={(e) => handleCountryMouseEnter("cust-country", e.currentTarget)}
                onMouseLeave={handleCountryMouseLeave}
              >
                <CountrySelect
                  value={isEntity ? customer.nationalityOrIncorporationCountry || "" : customer.country || ""}
                  onChange={isEntity ? handleNationalityOrIncChange : handleCountryChange}
                  className={`w-100 country-customerDetails kycCustomerDetailsHybridCountrySelect ${cc([
                    "customer",
                    isEntity ? "nationalityOrIncorporationCountry" : "country",
                  ])}`}
                  optionTextClassName="countryTextOptionsCustomerDetails"
                  disabled={readOnly}
                />
              </div>
              {(isEntity ? customer.nationalityOrIncorporationCountry : customer.country) ? <Tooltip id="cust-country" place="bottom" isOpen={openTooltip === "cust-country"} style={{ zIndex: 9999, backgroundColor: "#9E9692", borderRadius: 10, fontSize: 12 }} /> : null}
              <KycCustomerDetailsFieldError
                show={isEntity ? missingNationality : missingCountry}
              />
            </KycCustomerDetailsEditableCard>
          </div>

          <div className="col-12">
            <div className="row g-3">
              <div className="col-4">
                <KycCustomerDetailsEditableCard
                  icon={iconEmail}
                  label={copy.labels.email}
                  className="h-100"
                  topRightNote={
                    showEmailBlurError ? (
                      <>
                        <i
                          className="bi bi-exclamation-triangle-fill"
                          aria-hidden="true"
                        />{" "}
                        Invalid email address
                      </>
                    ) : null
                  }
                >
                  <input
                    type="email"
                    className={`form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--line ${cc([
                      "customer",
                      "email",
                    ])}`}
                    value={customer.email || ""}
                    placeholder={copy.labels.email}
                    onChange={(e) =>
                      onSetAt(["customer", "email"], e.target.value)
                    }
                    onBlur={onEmailBlur}
                    disabled={readOnly}
                  />
                  {missingEmail ? (
                    <InlineDangerMessage>This field is required</InlineDangerMessage>
                  ) : invalidEmail && !showEmailBlurError ? (
                    <InlineDangerMessage>
                      Please enter a valid email address
                    </InlineDangerMessage>
                  ) : null}
                </KycCustomerDetailsEditableCard>
              </div>

              <div className="col-4">
                <KycCustomerDetailsEditableCard
                  icon={iconPhoneCall}
                  label={copy.labels.phone}
                  className="h-100"
                >
                  <div className="kycCustomerDetailsHybridPhone">
                    <ClientPhoneField
                      initialValue={customer.phone || ""}
                      onChange={(value) =>
                        onSetAt(["customer", "phone"], sanitizePhone(value))
                      }
                      preferredCountries={[
                        isEntity
                          ? customer.nationalityOrIncorporationCountry || customer.country
                          : customer.country,
                        preferredPhoneCountry,
                      ]}
                      disabled={readOnly}
                    />
                  </div>
                  {missingPhone ? (
                    <InlineDangerMessage>This field is required</InlineDangerMessage>
                  ) : null}
                </KycCustomerDetailsEditableCard>
              </div>

              <div className="col-4">
                <KycCustomerDetailsEditableCard
                  icon={iconWebsite}
                  label={copy.labels.website}
                  className="h-100"
                  iconClassName="kycCustomerDetailsHybridIconImg--website"
                >
                  <input
                    className={`form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--line ${cc([
                      "customer",
                      "website",
                    ])}`}
                    value={customer.website || ""}
                    placeholder={copy.placeholders.website}
                    onChange={(e) =>
                      onSetAt(["customer", "website"], e.target.value)
                    }
                    disabled={readOnly}
                  />
                </KycCustomerDetailsEditableCard>
              </div>
            </div>
          </div>

          <div className="col-12">
            <KycCustomerDetailsEditableCard
              icon={iconBusinessActivity}
              label={copy.labels.businessActivity}
              className="h-100"
            >
              <textarea
                id="kyc-business-activity-field"
                ref={businessActivityRef}
                className={`form-control kycCustomerDetailsHybridInput kycCustomerDetailsHybridInput--line ${cc([
                  "customer",
                  "businessActivity",
                ])}`}
                rows={1}
                value={customer.businessActivity || ""}
                placeholder={copy.placeholders.businessActivity}
                onChange={(e) => {
                  autoResizeTextarea(e.currentTarget);
                  const val = capitalizeFirstLetter(e.target.value);
                  onSetAt(["customer", "businessActivity"], val);
                  debouncedSyncIndustryAssessment(val);
                }}
                onInput={(e) => autoResizeTextarea(e.currentTarget)}
                disabled={readOnly}
              />
              <KycCustomerDetailsFieldError show={missingBusinessActivity} />
            </KycCustomerDetailsEditableCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export function KycSectionCustomerDetailsOldStandard({
  sectionNo = 0,
  customer,
  preferredPhoneCountry,
  copy,
  mode,
  cc,
  readOnly,
  isEntity,
  missingName,
  missingNationality,
  missingDob,
  missingAddress,
  missingCity,
  missingCountry,
  missingEmail,
  invalidEmail,
  showEmailBlurError,
  onEmailBlur,
  missingPhone,
  missingBusinessActivity,
  onSetAt,
  handleNationalityOrIncChange,
  handleCountryChange,
  debouncedSyncIndustryAssessment,
  addressBoxRef,
  cityBoxRef,
  addressSuggestions,
  addressLoading,
  addressDropdownOpen,
  citySuggestions,
  cityLoading,
  cityDropdownOpen,
  handleAddressInput,
  handleCityInput,
  applyAddressSuggestion,
  applyCitySuggestion,
  commitCustomAddress,
  commitCustomCity,
}) {
  return (
    <div className="card kycCard kycCustomerDetailsCard mb-3">
      <div className="kycSectionHead">
        {copy.header.kicker ? (
          <div className="kycSectionKicker">{copy.header.kicker}</div>
        ) : null}

        <h5 className="kycSectionTitle">
          {sectionNo}
          {copy.header.title}
        </h5>

        {copy.header.hint ? (
          <div className="kycSectionHint">{copy.header.hint}</div>
        ) : null}
      </div>

      <div className="kycSectionBody">
        <div className="row g-3">
          <div className="col-3">
            <div className="text-muted small mb-1 titleInput">
              {copy.labels.type}
            </div>

            <select
              className={"form-select " + cc(["customer", "type"])}
              value={customer.type || "individual"}
              onChange={(e) => onSetAt(["customer", "type"], e.target.value)}
              disabled={mode === "client" || readOnly}
            >
              <option value="individual">Individual (Natural Person)</option>
              <option value="entity">Entity (Legal Person)</option>
            </select>
          </div>

          <div className={isEntity ? "col-7" : "col-4"}>
            <div className="text-muted small mb-1 titleInput">
              {copy.labels.name}
            </div>
            <input
              className={"form-control " + cc(["customer", "name"])}
              value={customer.name || ""}
              onChange={(e) =>
                onSetAt(
                  ["customer", "name"],
                  isEntity
                    ? e.target.value
                    : capitalizeNameWords(e.target.value)
                )
              }
              disabled={readOnly}
            />
            {missingName ? (
              <InlineDangerMessage>This field is required</InlineDangerMessage>
            ) : null}
          </div>

          {!isEntity ? (
            <div className="col-3 country-customerDetails">
              <div className="text-muted small mb-1 titleInput">
                {copy.labels.nationalityOrInc}
              </div>

              <CountrySelect
                value={customer.nationalityOrIncorporationCountry || ""}
                onChange={handleNationalityOrIncChange}
                className={
                  "w-100 " + cc(["customer", "nationalityOrIncorporationCountry"])
                }
                optionTextClassName="countryTextOptionsCustomerDetails"
                disabled={readOnly}
              />
              {missingNationality ? (
                <InlineDangerMessage>This field is required</InlineDangerMessage>
              ) : null}
            </div>
          ) : null}

          <div className="col-2">
            <div className="text-muted small mb-1 titleInput">
              {copy.labels.dobOrInc}
            </div>

            <div className={cc(["customer", "dobOrIncorporationDate"])}>
              <DatePickerField
                label={null}
                value={toDateOrNull(customer.dobOrIncorporationDate)}
                onChange={(val) =>
                  onSetAt(["customer", "dobOrIncorporationDate"], val)
                }
                dateFormat="d M yy"
                appendTo="body"
                disabled={readOnly}
                stagedView
              />
              {missingDob ? (
                <InlineDangerMessage>This field is required</InlineDangerMessage>
              ) : null}
            </div>
          </div>

          <div className="col-6">
            <div className="text-muted small mb-1 titleInput">
              {copy.labels.address}
            </div>
            <KycCustomerDetailsTypeaheadField
              kind="address"
              boxRef={addressBoxRef}
              className={"form-control " + cc(["customer", "address"])}
              value={customer.address || ""}
              placeholder={copy.placeholders.address}
              onChange={handleAddressInput}
              onCommit={commitCustomAddress}
              suggestions={addressSuggestions}
              loading={addressLoading}
              dropdownOpen={addressDropdownOpen}
              onSuggestionSelect={applyAddressSuggestion}
              disabled={readOnly}
            />
            {missingAddress ? (
              <InlineDangerMessage>This field is required</InlineDangerMessage>
            ) : null}
          </div>

          <div className="col-3">
            <div className="text-muted small mb-1 titleInput">
              {copy.labels.city}
            </div>
            <KycCustomerDetailsTypeaheadField
              kind="city"
              boxRef={cityBoxRef}
              className={"form-control " + cc(["customer", "city"])}
              value={customer.city || ""}
              placeholder={copy.placeholders.city}
              onChange={handleCityInput}
              onCommit={commitCustomCity}
              suggestions={citySuggestions}
              loading={cityLoading}
              dropdownOpen={cityDropdownOpen}
              onSuggestionSelect={applyCitySuggestion}
              disabled={readOnly}
            />
            {missingCity ? (
              <InlineDangerMessage>This field is required</InlineDangerMessage>
            ) : null}
          </div>

          <div className="col-3 country-customerDetails">
            <div className="text-muted small mb-1 titleInput">
              {copy.labels.country}
            </div>

            <CountrySelect
              value={customer.country || ""}
              onChange={handleCountryChange}
              className={"w-100 " + cc(["customer", "country"])}
              optionTextClassName="countryTextOptionsCustomerDetails"
              disabled={readOnly}
            />
            {missingCountry ? (
              <InlineDangerMessage>This field is required</InlineDangerMessage>
            ) : null}
          </div>

          <div className="col-4">
            <div className="text-muted small mb-1 titleInput position-relative">
              {copy.labels.email}
              {showEmailBlurError ? (
                <span className="kycCustomerDetailsTopRightNote kycCustomerDetailsTopRightNote--legacy">
                  <i
                    className="bi bi-exclamation-triangle-fill"
                    aria-hidden="true"
                  />{" "}
                  Invalid email address
                </span>
              ) : null}
            </div>
            <input
              type="email"
              className={"form-control " + cc(["customer", "email"])}
              value={customer.email || ""}
              onChange={(e) => onSetAt(["customer", "email"], e.target.value)}
              onBlur={onEmailBlur}
              disabled={readOnly}
            />
            {missingEmail ? (
              <InlineDangerMessage>This field is required</InlineDangerMessage>
            ) : invalidEmail && !showEmailBlurError ? (
              <InlineDangerMessage>
                Please enter a valid email address
              </InlineDangerMessage>
            ) : null}
          </div>

          <div className="col-4">
            <div className="text-muted small mb-1 titleInput">
              {copy.labels.phone}
            </div>
            <div className="kycCustomerDetailsHybridPhone">
              <ClientPhoneField
                initialValue={customer.phone || ""}
                onChange={(value) =>
                  onSetAt(["customer", "phone"], sanitizePhone(value))
                }
                preferredCountries={[
                  isEntity
                    ? customer.nationalityOrIncorporationCountry || customer.country
                    : customer.country,
                  preferredPhoneCountry,
                ]}
                disabled={readOnly}
              />
            </div>
            {missingPhone ? (
              <InlineDangerMessage>This field is required</InlineDangerMessage>
            ) : null}
          </div>

          <div className="col-4">
            <div className="text-muted small mb-1 titleInput">
              {copy.labels.website}
            </div>
            <input
              className={"form-control " + cc(["customer", "website"])}
              value={customer.website || ""}
              placeholder={copy.placeholders.website}
              onChange={(e) => onSetAt(["customer", "website"], e.target.value)}
              disabled={readOnly}
            />
          </div>

          <div className="col-12">
            <div className="text-muted small mb-1 titleInput">
              {copy.labels.businessActivity}
            </div>
            <input
              id="kyc-business-activity-field"
              className={"form-control " + cc(["customer", "businessActivity"])}
              value={customer.businessActivity || ""}
              placeholder={copy.placeholders.businessActivity}
              onChange={(e) => {
                const val = capitalizeFirstLetter(e.target.value);
                onSetAt(["customer", "businessActivity"], val);
                debouncedSyncIndustryAssessment(val);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              disabled={readOnly}
            />
            {missingBusinessActivity ? (
              <InlineDangerMessage>This field is required</InlineDangerMessage>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export const KycSectionCustomerDetails_old_version =
  KycSectionCustomerDetailsOldStandard;

export default function KycSectionCustomerDetails({
  sectionNo = 0,
  separateNames = false,
  form,
  onSetAt,
  mode = "staff", // "client" | "staff" | "review"
  changedClass, // optional function: (pathArray) => "kycChanged"
  readOnly = false,
  copyOverrides = {},
  submitAttempted = false,
  preferredPhoneCountry = "",
  useMainCountryOfOperation = false,
}) {
  const customer = form?.customer || {};
  const isEntity = customer.type === "entity";
  const showMainCountryOfOperation = isEntity && useMainCountryOfOperation;
  const [emailBlurred, setEmailBlurred] = useState(false);
  const missingName = submitAttempted && !String(customer.name || "").trim();
  const missingNationality =
    submitAttempted &&
    !String(customer.nationalityOrIncorporationCountry || "").trim();
  const missingMainCountryOfOperation =
    submitAttempted &&
    showMainCountryOfOperation &&
    !String(customer.mainCountryOfOperation || "").trim();
  const missingDob =
    submitAttempted && !toDateOrNull(customer.dobOrIncorporationDate);
  const missingAddress =
    submitAttempted && !String(customer.address || "").trim();
  const missingCity = submitAttempted && !String(customer.city || "").trim();
  const missingCountry =
    submitAttempted && !String(customer.country || "").trim();
  const missingEmail = submitAttempted && !String(customer.email || "").trim();
  const emailValue = String(customer.email || "").trim();
  const invalidEmail =
    submitAttempted &&
    emailValue &&
    !isValidEmail(customer.email || "");
  const showEmailBlurError =
    mode === "client" &&
    emailBlurred &&
    emailValue &&
    !isValidEmail(customer.email || "");
  const missingPhone = submitAttempted && !String(customer.phone || "").trim();
  const missingBusinessActivity =
    submitAttempted && !String(customer.businessActivity || "").trim();
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);

  // Reset industry assessment when field change

  // --- Industry reset/restore while typing (with debounce) ---
  const industryBackupRef = useRef(null); // stores previous result object
  const lastAssessedTextRef = useRef(""); // stores baseline assessed text
  const industryTypingTimerRef = useRef(null); // debounce timer
  const addressTimerRef = useRef(null);
  const addressAbortRef = useRef(null);
  const cityTimerRef = useRef(null);
  const cityAbortRef = useRef(null);
  const addressBoxRef = useRef(null);
  const cityBoxRef = useRef(null);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (industryTypingTimerRef.current) {
        clearTimeout(industryTypingTimerRef.current);
        industryTypingTimerRef.current = null;
      }
      if (addressTimerRef.current) {
        clearTimeout(addressTimerRef.current);
        addressTimerRef.current = null;
      }
      if (addressAbortRef.current) {
        addressAbortRef.current.abort();
        addressAbortRef.current = null;
      }
      if (cityTimerRef.current) {
        clearTimeout(cityTimerRef.current);
        cityTimerRef.current = null;
      }
      if (cityAbortRef.current) {
        cityAbortRef.current.abort();
        cityAbortRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    function onPointerDown(e) {
      if (!addressBoxRef.current?.contains(e.target)) {
        setAddressDropdownOpen(false);
      }
      if (!cityBoxRef.current?.contains(e.target)) {
        setCityDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, []);

  // If a NEW assessment happens, reset backup baseline logic
  useEffect(() => {
    const assessed = String(
      form?.internalUse?.industryRiskResult?.auto?.checkedText || ""
    ).trim();

    // ✅ If assessment got cleared to null, do NOT overwrite the baseline
    // (we need it to restore if the user types back)
    if (!assessed) return;

    if (assessed !== lastAssessedTextRef.current) {
      industryBackupRef.current = null;
      lastAssessedTextRef.current = assessed;
    }
  }, [form?.internalUse?.industryRiskResult?.auto?.checkedText]);

  useEffect(() => {
    if (!isEntity) return;

    const country = String(customer.country || "").trim();
    const incorporationCountry = String(
      customer.nationalityOrIncorporationCountry || ""
    ).trim();

    if (country && incorporationCountry !== country) {
      onSetAt?.(["customer", "nationalityOrIncorporationCountry"], country);
      return;
    }

    if (!country && incorporationCountry) {
      onSetAt?.(["customer", "country"], incorporationCountry);
    }
  }, [
    isEntity,
    customer.country,
    customer.nationalityOrIncorporationCountry,
    onSetAt,
  ]);

  function syncIndustryAssessmentWithTyping(nextVal) {
    const current = String(nextVal || "").trim();

    const assessedTextLive = String(
      form?.internalUse?.industryRiskResult?.auto?.checkedText || ""
    ).trim();

    // ✅ Use live baseline if available, otherwise use remembered baseline
    const baselineAssessedText =
      assessedTextLive || lastAssessedTextRef.current;

    // Nothing assessed yet => do nothing
    if (!baselineAssessedText) return;

    const saved = form?.internalUse?.industryRiskResult;

    // If user is back to the assessed text => restore previous assessment (if it was cleared)
    if (current === baselineAssessedText) {
      if (saved === null && industryBackupRef.current) {
        onSetAt?.(
          ["internalUse", "industryRiskResult"],
          industryBackupRef.current
        );
      }
      return;
    }

    // If text differs => clear assessment (backup once)
    if (saved !== null) {
      industryBackupRef.current = saved;
      onSetAt?.(["internalUse", "industryRiskResult"], null);
    }
  }

  function debouncedSyncIndustryAssessment(nextVal) {
    const DEBOUNCE_MS = 250;

    if (industryTypingTimerRef.current) {
      clearTimeout(industryTypingTimerRef.current);
    }

    industryTypingTimerRef.current = setTimeout(() => {
      syncIndustryAssessmentWithTyping(nextVal);
    }, DEBOUNCE_MS);
  }

  // ✅ Safe wrapper: if changedClass not provided, return ""
  const cc = (path) =>
    typeof changedClass === "function" ? changedClass(path) : "";

  // ✅ Copy by mode (titles + wording)
  const COPY = {
    client: {
      header: {
        kicker: "Section 1",
        title: " | Your details",
        hint: isEntity
          ? "Please provide your entity details as our client"
          : "Please provide your personal details as our client",
      },
      labels: {
        type: "Completing as",
        name: isEntity ? "Legal name" : "Full name",
        nationalityOrInc: isEntity ? "Country of incorporation" : "Nationality",
        mainCountryOfOperation: "Main country of operation",
        dobOrInc: isEntity ? "Date of incorporation" : "Date of birth",
        email: "Email",
        phone: "Phone number",
        address: isEntity ? "Registered address" : "Address",
        // address: "Address",
        city: "City",
        country: isEntity ? "Country of incorporation" : "Country",
        website: "Website (if any)",
        businessActivity: "Business activity",
      },
      placeholders: {
        address: "Street / building / PO Box…",
        city: "e.g. Dubai",
        website: "e.g. https://example.com",
        businessActivity:
          isEntity
            ? "Describe the main business activity of the company in sufficient detail"
            : "Describe the individual’s main professional or business activity in sufficient detail",
      },
    },

    staff: {
      header: {
        kicker: null,
        title: " | Customer identification",
        hint: null,
      },
      labels: {
        type: "Customer type",
        name: isEntity ? "Entity name" : "Full name",
        nationalityOrInc: isEntity ? "Incorporation country" : "Nationality",
        mainCountryOfOperation: "Main country of operation",
        dobOrInc: isEntity ? "Date of incorporation" : "Date of birth",
        email: "Email",
        phone: "Phone number",
        address: isEntity ? "Registered address" : "Address",
        // address: "Registered address",
        city: "City",
        country: isEntity ? "Country of incorporation" : "Country",
        website: "Website",
        businessActivity: "Business activity",
      },
      placeholders: {
        address: "",
        city: "",
        website: "",
        businessActivity:
          "Describe the individual’s main professional or business activity in sufficient detail",
      },
    },

    review: {
      header: {
        kicker: null,
        title: " | Customer identification",
        hint: null,
      },
      labels: {
        type: "Customer type",
        name: isEntity ? "Entity name" : "Full name",
        nationalityOrInc: isEntity ? "Incorporation country" : "Nationality",
        mainCountryOfOperation: "Main country of operation",
        dobOrInc: isEntity ? "Date of incorporation" : "Date of birth",
        email: "Email",
        phone: "Phone number",
        address: isEntity ? "Registered address" : "Address",
        // address: "Address",
        city: "City",
        country: isEntity ? "Country of incorporation" : "Country",
        website: "Website",
        businessActivity: "Business activity",
      },
      placeholders: {
        address: "",
        city: "",
        website: "",
        businessActivity:
          "Describe the individual’s main professional or business activity in sufficient detail",
      },
    },
  };

  // ✅ merge default copy + overrides
  const baseCopy = COPY[mode] || COPY.staff;

  const copy = {
    ...baseCopy,
    ...copyOverrides,
    header: { ...baseCopy.header, ...(copyOverrides.header || {}) },
    labels: { ...baseCopy.labels, ...(copyOverrides.labels || {}) },
    placeholders: {
      ...baseCopy.placeholders,
      ...(copyOverrides.placeholders || {}),
    },
  };

  // ✅ entity logic: keep incorporation country and country aligned
  function handleNationalityOrIncChange(val) {
    onSetAt(["customer", "nationalityOrIncorporationCountry"], val || "");

    // entity: also fill country
    if (isEntity) {
      onSetAt(["customer", "country"], val || "");
    }
  }

  function handleMainCountryOfOperationChange(val) {
    onSetAt(["customer", "mainCountryOfOperation"], val || "");
  }

  function handleCountryChange(val) {
    onSetAt(["customer", "country"], val || "");

    // entity: also fill incorporation country
    if (isEntity) {
      onSetAt(["customer", "nationalityOrIncorporationCountry"], val || "");
    }
  }

  function applyAddressSuggestion(item) {
    onSetAt?.(["customer", "address"], capitalizeFirstLetter(item.address || ""));
    if (item.city) onSetAt?.(["customer", "city"], capitalizeFirstLetter(item.city));
    if (item.country) {
      onSetAt?.(["customer", "country"], item.country);
      if (isEntity) {
        onSetAt?.(["customer", "nationalityOrIncorporationCountry"], item.country);
      }
    }
    setAddressSuggestions([]);
    setAddressDropdownOpen(false);
  }

  function applyCitySuggestion(item) {
    onSetAt?.(["customer", "city"], capitalizeFirstLetter(item.city || ""));
    if (item.country) {
      onSetAt?.(["customer", "country"], item.country);
      if (isEntity) {
        onSetAt?.(["customer", "nationalityOrIncorporationCountry"], item.country);
      }
    }
    setCitySuggestions([]);
    setCityDropdownOpen(false);
  }

  async function fetchAddressSuggestions(query) {
    if (!MAPBOX_TOKEN || readOnly) {
      setAddressSuggestions([]);
      setAddressLoading(false);
      return;
    }

    const trimmed = String(query || "").trim();
    if (trimmed.length < 3) {
      setAddressSuggestions([]);
      setAddressLoading(false);
      return;
    }

    if (addressAbortRef.current) {
      addressAbortRef.current.abort();
    }

    const controller = new AbortController();
    addressAbortRef.current = controller;
    setAddressLoading(true);

    try {
      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          trimmed
        )}.json` +
        `?access_token=${encodeURIComponent(MAPBOX_TOKEN)}` +
        `&autocomplete=true&types=address,place,locality,neighborhood`;

      const res = await fetch(url, { signal: controller.signal });
      const json = await res.json().catch(() => ({}));
      const items = Array.isArray(json?.features)
        ? json.features.map(parseMapboxFeature).filter(Boolean)
        : [];
      setAddressSuggestions(items);
      setAddressDropdownOpen(items.length > 0);
    } catch (err) {
      if (err?.name !== "AbortError") {
        setAddressSuggestions([]);
        setAddressDropdownOpen(false);
      }
    } finally {
      if (addressAbortRef.current === controller) {
        addressAbortRef.current = null;
      }
      setAddressLoading(false);
    }
  }

  async function fetchCitySuggestions(query) {
    if (!MAPBOX_TOKEN || readOnly) {
      setCitySuggestions([]);
      setCityLoading(false);
      return;
    }

    const trimmed = String(query || "").trim();
    if (trimmed.length < 2) {
      setCitySuggestions([]);
      setCityLoading(false);
      return;
    }

    if (cityAbortRef.current) {
      cityAbortRef.current.abort();
    }

    const controller = new AbortController();
    cityAbortRef.current = controller;
    setCityLoading(true);

    try {
      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          trimmed
        )}.json` +
        `?access_token=${encodeURIComponent(MAPBOX_TOKEN)}` +
        `&autocomplete=true&types=place,locality,district`;

      const res = await fetch(url, { signal: controller.signal });
      const json = await res.json().catch(() => ({}));
      const items = Array.isArray(json?.features)
        ? json.features.map(parseMapboxCityFeature).filter(Boolean)
        : [];
      setCitySuggestions(items);
      setCityDropdownOpen(items.length > 0);
    } catch (err) {
      if (err?.name !== "AbortError") {
        setCitySuggestions([]);
        setCityDropdownOpen(false);
      }
    } finally {
      if (cityAbortRef.current === controller) {
        cityAbortRef.current = null;
      }
      setCityLoading(false);
    }
  }

  function handleAddressInput(value) {
    onSetAt?.(["customer", "address"], capitalizeFirstLetter(value));

    if (addressTimerRef.current) {
      clearTimeout(addressTimerRef.current);
    }

    const trimmed = String(value || "").trim();
    if (!MAPBOX_TOKEN || trimmed.length < 3) {
      setAddressSuggestions([]);
      setAddressDropdownOpen(false);
      setAddressLoading(false);
      return;
    }

    addressTimerRef.current = setTimeout(() => {
      fetchAddressSuggestions(trimmed);
    }, 280);
  }

  function commitCustomAddress() {
    if (addressTimerRef.current) {
      clearTimeout(addressTimerRef.current);
      addressTimerRef.current = null;
    }
    if (addressAbortRef.current) {
      addressAbortRef.current.abort();
      addressAbortRef.current = null;
    }
    setAddressLoading(false);
    setAddressSuggestions([]);
    setAddressDropdownOpen(false);
  }

  function handleCityInput(value) {
    onSetAt?.(["customer", "city"], capitalizeFirstLetter(value));

    if (cityTimerRef.current) {
      clearTimeout(cityTimerRef.current);
    }

    const trimmed = String(value || "").trim();
    if (!MAPBOX_TOKEN || trimmed.length < 2) {
      setCitySuggestions([]);
      setCityDropdownOpen(false);
      setCityLoading(false);
      return;
    }

    cityTimerRef.current = setTimeout(() => {
      fetchCitySuggestions(trimmed);
    }, 240);
  }

  function commitCustomCity() {
    if (cityTimerRef.current) {
      clearTimeout(cityTimerRef.current);
      cityTimerRef.current = null;
    }
    if (cityAbortRef.current) {
      cityAbortRef.current.abort();
      cityAbortRef.current = null;
    }
    setCityLoading(false);
    setCitySuggestions([]);
    setCityDropdownOpen(false);
  }

  const sharedProps = {
    sectionNo,
    separateNames,
    customer,
    preferredPhoneCountry,
    copy,
    mode,
    cc,
    readOnly,
    isEntity,
    useMainCountryOfOperation,
    missingName,
    missingNationality,
    missingMainCountryOfOperation,
    missingDob,
    missingAddress,
    missingCity,
    missingCountry,
    missingEmail,
    invalidEmail,
    showEmailBlurError,
    onEmailBlur: () => setEmailBlurred(true),
    missingPhone,
    missingBusinessActivity,
    onSetAt,
    handleNationalityOrIncChange,
    handleMainCountryOfOperationChange,
    handleCountryChange,
    debouncedSyncIndustryAssessment,
    addressBoxRef,
    cityBoxRef,
    addressSuggestions,
    addressLoading,
    addressDropdownOpen,
    citySuggestions,
    cityLoading,
    cityDropdownOpen,
    handleAddressInput,
    handleCityInput,
    applyAddressSuggestion,
    applyCitySuggestion,
    commitCustomAddress,
    commitCustomCity,
  };

  if (!USE_OLD_STANDARD_CUSTOMER_DETAILS_LAYOUT) {
    return <KycSectionCustomerDetailsClientViewInspired {...sharedProps} />;
  }

  return <KycSectionCustomerDetailsOldStandard {...sharedProps} />;
}
